import { Auth } from '@aws-amplify/auth';
import { GetDataEndpointCommand, KinesisVideoClient, ListStreamsCommand } from '@aws-sdk/client-kinesis-video';
import { KinesisVideoArchivedMediaClient } from '@aws-sdk/client-kinesis-video-archived-media';
import { streamNameFromHubAndCameraAssetName } from '@iotv/datamodel-core';
import { Milliseconds } from '@iotv/iotv-v3-types';
import { Environment } from 'internal-config';
import { useContext, useEffect, useRef } from 'react';
import { KinesisVideoStreamContext } from '../Context/KinesisVideoStreamContext';
const { region, stage, projectCode } = Environment;
const protocol = 'NOT_DASH';
const checkIsLiveIntervalInMs = Milliseconds.SECOND * 15;
export const useKVStreams = ({}) => {
    const { state, dispatch } = useContext(KinesisVideoStreamContext);
    const { VideoHub, VideoStream, kvClient, selectedStreamInfo } = state;
    const isSettingKvClientRef = useRef(false);
    const clearResolvedStreamState = (fetchVideoStreamError = null) => {
        dispatch({
            selectedStreamInfo: null,
            StreamInfoList: null,
            DataEndpoint: null,
            kvaClient: null,
            HLSStreamingSessionURL: null,
            HLSListFragmentsURL: null,
            fetchVideoStreamError,
        });
    };
    const setKVClient = async () => {
        if (isSettingKvClientRef.current || state.kvClient) {
            return;
        }
        isSettingKvClientRef.current = true;
        try {
            const credentials = await Auth.currentUserCredentials();
            const newKvClient = new KinesisVideoClient({ region, credentials });
            dispatch({ kvClient: newKvClient });
            console.log('KinesisVideoClient initialized in useKVStreams');
        }
        catch (e) {
            console.error(`setKVClient ${e?.message ?? e}`);
        }
        finally {
            isSettingKvClientRef.current = false;
        }
    };
    const fetchDataEndpoint = async ({ selectedStreamInfo, kvClient }) => {
        if (kvClient && selectedStreamInfo) {
            console.log('Fetching data endpoint');
            try {
                const { DataEndpoint } = await kvClient.send(new GetDataEndpointCommand({
                    StreamARN: selectedStreamInfo.StreamARN,
                    APIName: protocol === 'DASH' ? 'GET_DASH_STREAMING_SESSION_URL' : 'GET_HLS_STREAMING_SESSION_URL',
                }));
                console.log(`Data Endpoint ${DataEndpoint}`);
                const credentials = await Auth.currentUserCredentials();
                const kvaClient = new KinesisVideoArchivedMediaClient({ region, credentials, endpoint: DataEndpoint });
                dispatch({ DataEndpoint, kvaClient, fetchVideoStreamError: null });
            }
            catch (e) {
                console.error(`Fetch Endpoint ${e.message}`);
                clearResolvedStreamState(`FetchDataEndpoint ${e?.message ?? e}`);
            }
        }
        else {
            console.log(`Can't fetch data endpoint, kvClient or selectedStreamInfo is null`, kvClient, selectedStreamInfo);
            clearResolvedStreamState(kvClient ? 'No stream selected' : null);
        }
    };
    const listStreams = async ({ kvClient, VideoStream, VideoHub }) => {
        const streamName = VideoStream?.kinesisVideoStreamName ?? (VideoStream?.name && VideoHub?.name ? streamNameFromHubAndCameraAssetName({ hubName: VideoHub.name, cameraName: VideoStream.name }) : null);
        if (kvClient && streamName) {
            const params = {
                NextToken: undefined,
                StreamNameCondition: { ComparisonOperator: 'BEGINS_WITH', ComparisonValue: streamName },
            };
            const command = new ListStreamsCommand(params);
            try {
                const { StreamInfoList, NextToken } = await kvClient.send(command);
                const newSelectedStreamInfo = StreamInfoList?.find((streamInfo) => streamInfo.StreamName === streamName) ?? null;
                console.log(`Looked for ${streamName} and selected stream for ${VideoHub?.name} ${VideoStream?.name}`, newSelectedStreamInfo);
                if (newSelectedStreamInfo === null) {
                    console.log(`No stream found matching ${streamName} with `, StreamInfoList);
                }
                if (newSelectedStreamInfo === null) {
                    console.log(`selected stream not found:`, StreamInfoList);
                    clearResolvedStreamState(`No stream found for ${streamName}`);
                    dispatch({ StreamInfoList });
                    return;
                }
                dispatch({ selectedStreamInfo: newSelectedStreamInfo, StreamInfoList, fetchVideoStreamError: null });
                await fetchDataEndpoint({ selectedStreamInfo: newSelectedStreamInfo, kvClient });
            }
            catch (e) {
                console.error(`ListStreams ${e.message} with ${streamName}`);
                clearResolvedStreamState(`ListStreams ${e?.message ?? e}`);
            }
        }
        else {
            console.log(`listStreams called but kvClient or streamName is null`, { kvClient: !!kvClient, streamName, VideoStream: !!VideoStream, VideoHub: !!VideoHub });
            if (!streamName) {
                clearResolvedStreamState(null);
            }
        }
    };
    useEffect(() => {
        if (!kvClient) {
            void setKVClient();
        }
        if (kvClient && VideoStream?.sk && VideoHub?.sk) {
            console.log(`kvClient, VideoStream?.sk, VideoHub?.sk are all non-null, listing streams`, VideoStream?.name, VideoHub?.name);
            listStreams({ kvClient, VideoStream, VideoHub });
        }
        else {
            console.log(`kvClient or VideoStream?.sk or VideoHub?.sk is null, not listing streams`, kvClient, VideoStream?.sk, VideoHub?.sk);
        }
    }, [kvClient, VideoStream?.sk, VideoHub?.sk]);
    return { streamName: selectedStreamInfo?.StreamName };
};
